// Public page: rendered without authentication (see App.js). The Chrome Web
// Store listing for the Carspace extension requires a privacy policy URL that
// is reachable by anyone.
const styles = {
    // The app's global stylesheet paints a dark background, so the page
    // carries its own full-height light backdrop to stay readable.
    backdrop: { minHeight: "100vh", background: "#ffffff" },
    page: {
        maxWidth: "860px",
        margin: "0 auto",
        padding: "48px 24px 96px",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        color: "#1f2937",
        lineHeight: 1.7,
    },
    h1: { fontSize: "2rem", marginBottom: "4px", color: "#111827" },
    updated: { color: "#6b7280", marginBottom: "32px" },
    h2: { fontSize: "1.25rem", marginTop: "36px", marginBottom: "12px", color: "#111827" },
};

function ExtensionPrivacyPolicy() {
    return (
        <div style={styles.backdrop}>
        <div style={styles.page}>
            <h1 style={styles.h1}>Carspace Browser Extension Privacy Policy</h1>
            <p style={styles.updated}>Last updated: August 2, 2026</p>

            <p>
                This Privacy Policy describes how Carspace ("we", "us", or "our") collects, uses, and
                discloses information when you install and use the Carspace browser extension (the
                "Extension"). The Extension is a professional tool for managing vehicle auction activity on
                Copart (copart.com) and IAAI (iaai.com) and works together with a Carspace account issued to
                you by us (the "Service").
            </p>
            <p>
                By installing or using the Extension, you agree to the collection, use, and disclosure of
                your information as described in this Privacy Policy. If you do not agree, please do not
                install or use the Extension.
            </p>

            <h2 style={styles.h2}>Information We Collect</h2>
            <ul>
                <li>
                    <strong>Account information.</strong> When you sign in to the Extension, we collect your
                    Carspace account credentials (username and password, used only to authenticate you with
                    the Service) and your profile details such as your name and role.
                </li>
                <li>
                    <strong>Auction activity.</strong> The Extension records the auction-related actions you
                    take through it — such as bids placed, bidding history, watchlist entries, won vehicles,
                    and related account actions — and sends them to our servers so that they can be shown in
                    your dashboard and reviewed by your organization's administrators.
                </li>
                <li>
                    <strong>Auction site sessions and cookies.</strong> To automate sign-in and bidding on
                    the auction accounts assigned to you, the Extension manages browser sessions and cookies
                    on copart.com and iaai.com (including their sign-in and live-auction subdomains). These
                    are used solely to operate those auction sessions.
                </li>
                <li>
                    <strong>Technical information.</strong> The Extension collects your public IP address
                    and basic technical information needed to operate the Service, secure accounts, and
                    investigate misuse.
                </li>
                <li>
                    <strong>Locally stored data.</strong> Settings such as your language preference and
                    session state are stored locally in your browser's extension storage.
                </li>
            </ul>

            <h2 style={styles.h2}>How We Use Information</h2>
            <ul>
                <li>To authenticate you and operate the features of the Extension and the Service.</li>
                <li>
                    To automate and manage auction sessions, bidding, and account activity on Copart and
                    IAAI on your behalf.
                </li>
                <li>
                    To keep records of bidding and account activity for reporting and administration within
                    your organization.
                </li>
                <li>
                    To detect, investigate, or take action regarding possible fraudulent, unauthorized, or
                    malicious activity, and to protect the Service and its users.
                </li>
                <li>To provide customer support and improve the Service.</li>
            </ul>

            <h2 style={styles.h2}>Browser Permissions</h2>
            <p>
                The Extension requests browser permissions (such as storage, cookies, web request access,
                scripting, and debugging) solely to provide its core functionality: automating sign-in,
                monitoring live auctions, and placing bids on copart.com and iaai.com. The Extension is
                intended to be used on auction websites and does not collect your browsing history or the
                content of unrelated websites.
            </p>

            <h2 style={styles.h2}>How We Share Information</h2>
            <p>
                Information collected by the Extension is transmitted to our own servers. We do not sell
                your personal information. We may disclose information:
            </p>
            <ul>
                <li>
                    To vendors or service providers who perform services on our behalf (e.g., hosting, IT
                    management).
                </li>
                <li>
                    Within your organization — administrators of your Carspace account can see the activity
                    recorded through the Extension.
                </li>
                <li>
                    To comply with applicable legal obligations (including responding to subpoenas or
                    similar lawful requests), to enforce applicable terms, and to protect or defend the
                    Service, our rights, and the rights of our users or others.
                </li>
            </ul>

            <h2 style={styles.h2}>Data Security and Retention</h2>
            <p>
                We take reasonable measures to protect the information we collect. However, no security
                measures are perfect or impenetrable, and we cannot guarantee "perfect security." We retain
                information for as long as necessary to provide the Service, comply with our legal
                obligations, resolve disputes, and enforce our agreements. You are responsible for keeping
                your account credentials safe; if you believe your account has been compromised, please
                contact us immediately.
            </p>

            <h2 style={styles.h2}>Children's Data</h2>
            <p>
                The Extension is a professional tool, is not intended for children, and we do not knowingly
                collect any personal information about children.
            </p>

            <h2 style={styles.h2}>Changes to This Privacy Policy</h2>
            <p>
                We may update this Privacy Policy from time to time. We will post the revised Privacy Policy
                on this page and update the "Last updated" date.
            </p>

            <h2 style={styles.h2}>Your Rights</h2>
            <p>
                Depending on where you live, you may have rights in relation to your personal information,
                such as the right to access, correct, or delete the personal information we hold about you.
                To exercise any of these rights, contact us using the details below.
            </p>

            <h2 style={styles.h2}>Contact</h2>
            <p>
                Should you have any questions about our privacy practices or this Privacy Policy, please
                email us at <a href="mailto:info@carexpress.ge">info@carexpress.ge</a>.
            </p>
        </div>
        </div>
    );
}

export default ExtensionPrivacyPolicy;
